/**
 * Standalone Agent Chat Page
 * Path: /agent/[agentId]/chat/[id] - for specific chat
 * Path: /agent/[agentId]/chat/new - for new chat
 * 
 * This is a standalone chat interface outside the main app layout,
 * used when users launch an agent directly from the agents grid.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { getAuthHeader } from '@/utils/auth-storage'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { Chat } from '@/components/chat/chat'
import { InteractiveChatProvider } from '@/components/chat/interactive/interactive-context'
import { AgentChatLayout } from '@/components/layout/agent-chat'
import { AgentAccessRequired } from '@/components/agent-access-required'
import { DomainNotFoundPage } from '@/components/domain-not-found-page'
import { usePageTitle } from '@/hooks/use-page-title'
import { useAgent } from '@/hooks/use-agents'
import { chatService } from '@/services/chat-service'
import { useChatEvents } from '@/hooks/use-chat-events'
import { useConditionalAuth } from '@/components/auth/conditional-auth-provider'
import { usePublicEmbedConfig, applyEmbedPrimaryColor } from '@/hooks/use-public-embed-config'
import { useEmbedLocale } from '@/lib/embed-locale'
import { EmbedLanguageGate } from '@/components/chat/embed-language-gate'
import { notifyEmbedSurfaceReady } from '@/hooks/use-bubble-embed'
import type { ChatMessage } from '@/types/chat-message'

interface ChatData {
  id: string
  title: string
  sandboxId?: string
  metadata?: Record<string, unknown>
}

/**
 * Narrow error shape used by chat loading flows.
 * Some request helpers surface status metadata outside the standard Error type.
 */
interface ChatLoadErrorShape {
  /** Top-level HTTP-like status code exposed by the request helper. */
  status?: number
  /** Nested response metadata preserved by some transport wrappers. */
  response?: {
    /** HTTP status code from the underlying response object. */
    status?: number
  }
  /** Fallback numeric code used by legacy callers. */
  code?: number
}

/**
 * Detects whether a chat loading failure maps to a missing chat response.
 * Keeps redirect logic typed without relying on `any`.
 */
function isMissingChatError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }

  const chatError = error as ChatLoadErrorShape
  return (
    chatError.status === 404
    || chatError.response?.status === 404
    || chatError.code === 404
  )
}

export function StandaloneAgentChatPage() {
  const navigate = useNavigate()
  const chatEvents = useChatEvents()
  const { agentId, id } = useParams<{ agentId: string; id: string }>()
  const [searchParams] = useSearchParams()

  // Get conditional auth context to determine mode
  // This provides embed mode and bubble mode detection
  const { isEmbedMode, isBubbleMode, isAuthenticated } = useConditionalAuth()

  const { agent, loading: agentLoading, error: agentError } = useAgent(agentId || null)

  /**
   * Embed language gate (spec 075 — Phase 3). The public embed config carries
   * the published agent's language settings; when a gate is required and no
   * session locale exists yet, the gate renders INSTEAD of the chat.
   */
  const { config: embedConfig, resolved: embedConfigResolved } =
    usePublicEmbedConfig(agentId ?? null, isEmbedMode)
  const embedLocale = useEmbedLocale()
  const gateLanguages = embedConfig?.embed?.languages ?? []
  const needsLanguageGate =
    isEmbedMode &&
    Boolean(embedConfig?.embed?.requireLanguageGate) &&
    gateLanguages.length > 0 &&
    !embedLocale
  // Re-render on selection (the store also notifies, but keep it explicit)
  const [, setGatePassedAt] = useState(0)

  /**
   * Brand theming for the whole embed surface — applied at the PAGE level so
   * it covers the language gate too, not just the mounted <Chat> (which has
   * its own application for the main-app-independent path).
   */
  useEffect(() => {
    if (isEmbedMode) applyEmbedPrimaryColor(embedConfig?.primaryColor)
  }, [isEmbedMode, embedConfig?.primaryColor])

  const [chat, setChat] = useState<ChatData | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Determine if this is a new chat or existing chat
  const isNewChat = id === 'new'

  // Track the previous ID to detect when we navigate to /new from another chat
  const previousIdRef = useRef<string | undefined>(undefined)

  // Track the current new chat ID to ensure it persists during a single new chat session
  const currentNewChatIdRef = useRef<string | null>(null)

  // Generate a fresh UUID for new chats
  // CRITICAL: Must regenerate when navigating to /new to avoid using stale chat ID
  // This fixes the bug where clicking "New Chat" still sends messages to previous chat
  // We use a ref to track the current new chat ID, and only regenerate when we explicitly
  // navigate to /new from another chat (not on initial mount)
  const chatId = useMemo(() => {
    if (isNewChat) {
      // Check if we just navigated to /new from an existing chat
      const prevId = previousIdRef.current
      if (prevId !== undefined && prevId !== 'new') {
        // We navigated to /new from a different chat - generate a fresh UUID
        currentNewChatIdRef.current = uuidv4()
      } else if (currentNewChatIdRef.current === null) {
        // Initial mount on /new - generate the first UUID
        currentNewChatIdRef.current = uuidv4()
      }
      return currentNewChatIdRef.current
    }
    // For existing chats, use the ID from URL
    return id!
  }, [isNewChat, id])

  // Track the previous ID to detect navigation changes
  useEffect(() => {
    previousIdRef.current = id
  }, [id])

  const pageTitle = usePageTitle(
    agent ? `${agent.name} - ${chat?.title || 'New Chat'}` : 'Agent Chat',
    'Chat with your AI employee'
  )

  /**
   * Tell the bubble loader its overlay can go, as soon as this page settles on
   * a screen the visitor can actually use.
   *
   * The loader only drops the overlay on this signal, and until now the sole
   * sender was `<Chat>` (via `useBubbleEmbed`). The language gate renders
   * INSTEAD of `<Chat>`, so every first-time visitor to a gated agent waited
   * out the loader's timeout while staring at an overlay with a finished gate
   * beneath it.
   *
   * `hasSettledScreen` mirrors the early-return ladder below — true for every
   * branch that paints something real, false while an intermediate spinner is
   * up, so the overlay is never dropped onto a spinner. `agent || agentError`
   * is part of it because `useAgent` starts with `loading: false`: on the very
   * first commit the agent is merely "not fetched yet", and signalling there
   * would release the overlay before anything meaningful exists.
   *
   * This only decides WHEN to signal — it changes nothing about what renders.
   */
  const hasSettledScreen =
    !agentId ||
    (!agentLoading &&
      Boolean(agent || agentError) &&
      !(loading && !isNewChat) &&
      !(isEmbedMode && !embedConfigResolved))

  useEffect(() => {
    if (hasSettledScreen) {
      notifyEmbedSurfaceReady({ chatId, agentId })
    }
  }, [hasSettledScreen, chatId, agentId])

  // Load existing chat data (if not new chat and not bubble mode)
  useEffect(() => {
    if (!agentId) {
      setError('Agent ID is required')
      setLoading(false)
      return
    }

    // Wait for authentication to complete before making API calls
    // In embed mode with API key, wait for token exchange to complete
    if (isEmbedMode && !isAuthenticated) {
      setLoading(true)
      return
    }

    // In session mode, wait for session validation
    if (!isEmbedMode && !isAuthenticated) {
      setLoading(true)
      return
    }

    // In bubble mode, always treat as new chat regardless of ID
    // Bubble mode doesn't support chat history
    if (isNewChat || isBubbleMode) {
      // Reset state for new chat or bubble mode
      setChat(null)
      setMessages([])
      setError(null)
      setLoading(false)
      return
    }

    if (!id) {
      setError('Chat ID is required')
      setLoading(false)
      return
    }

    const loadChatData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Load chat details and messages in parallel
        const [chatResponse, messagesResponse] = await Promise.all([
          chatService.getChatById(id),
          chatService.getChatMessages(id),
        ])

        if (!chatResponse.success || !chatResponse.data) {
          setError('Chat not found')
          return
        }

        // Verify chat belongs to the agent
        if (chatResponse.data.agentId && chatResponse.data.agentId !== agentId) {
          setError('Chat not found for this agent')
          return
        }

        const chatData: ChatData = {
          id: chatResponse.data.id,
          title: chatResponse.data.title,
          sandboxId: chatResponse.data.sandboxId,
          metadata: chatResponse.data.metadata as Record<string, unknown> | undefined,
        }

        setChat(chatData)
        setMessages(messagesResponse.success ? messagesResponse.data : [])
      } catch (err: unknown) {
        console.error('Failed to load chat data:', err)
        // Auto-redirect to new chat if the error is a 404 (chat not found)
        // This handles mobile browsers with cached URLs to deleted chats
        if (isMissingChatError(err)) {
          // Use window.location.replace() instead of navigate() for mobile browsers
          // This forces a full browser navigation and bypasses cached URLs
          window.location.replace(`/agent/${agentId}/chat/new`)
        } else {
          setError('Failed to load chat')
        }
      } finally {
        setLoading(false)
      }
    }

    loadChatData()
  }, [agentId, id, isNewChat, isBubbleMode, isEmbedMode, isAuthenticated])

  // Listen for chat deletion events (not applicable in bubble mode)
  useEffect(() => {
    if (!id || isNewChat || isBubbleMode) return

    const unsubscribe = chatEvents.subscribe('delete-chat', (deletedChatId) => {
      if (deletedChatId === id) {
        // Redirect to new chat for this agent
        navigate(`/agent/${agentId}/chat/new`, { replace: true })
      }
    })

    return unsubscribe
  }, [id, isNewChat, isBubbleMode, chatEvents, navigate, agentId])

  /**
   * Detect if this chat has an active background stream (trigger-initiated).
   * The custom transport in Chat handles retry logic for connecting to the
   * stream — no polling or remounting needed here.
   */
  const isBackgroundStreaming = chat?.metadata?.status === 'streaming'

  /**
   * Subscribe to trigger lifecycle SSE events for this chat.
   * When the server emits 'stream-completed', update local state so the
   * streaming indicator clears and final messages are loaded from DB.
   */
  useEffect(() => {
    if (!isBackgroundStreaming || !id || isNewChat) return

    const serverUrl = import.meta.env.VITE_API_BASE_URL || ''
    const cleanUrl = serverUrl.replace(/\/api$/, '')
    const sseUrl = `${cleanUrl}/api/chat/trigger-events`

    let cancelled = false
    let abortCtrl: AbortController

    const connect = async () => {
      if (cancelled) return
      abortCtrl = new AbortController()

      try {
        const response = await fetch(sseUrl, {
          headers: { Authorization: getAuthHeader() || '', Accept: 'text/event-stream' },
          signal: abortCtrl.signal,
        })
        if (!response.ok || !response.body) return

        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
        let buf = ''

        while (!cancelled) {
          const { done, value } = await reader.read()
          if (done) break
          buf += value
          const lines = buf.split('\n')
          buf = lines.pop() || ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            try {
              const evt = JSON.parse(line.slice(6))
              if (evt.chatId === id && evt.type === 'stream-completed') {
                setChat(prev =>
                  prev ? { ...prev, metadata: { ...prev.metadata, status: 'completed' } } : prev
                )
              }
            } catch { /* ignore parse errors */ }
          }
        }
      } catch {
        if (!cancelled) setTimeout(connect, 5000)
      }
    }

    connect()
    return () => {
      cancelled = true
      abortCtrl?.abort()
    }
  }, [isBackgroundStreaming, id, isNewChat])

  /**
   * Handle new chat creation after first streaming response completes.
   * The client-generated UUID is used as the DB primary key, so the chat
   * already exists in the database by the time this fires.
   */
  const handleNewChatCreated = useCallback((newChatId: string) => {
    // In bubble mode, don't navigate to preserve embed parameters
    if (isBubbleMode) {
      setChat({ id: newChatId, title: `Chat with ${agent?.name || 'Agent'}` })
      return
    }

    // For normal and embed modes, navigate to the actual chat URL
    // At this point, the chat exists in the database (created by Agent during streaming)
    const params = new URLSearchParams()
    if (isEmbedMode) {
      // Preserve embed parameters. Prefer the public embed key; keep the legacy
      // apikey param working for back-compat with older live embeds.
      const embedKey = searchParams.get('embedKey')
      if (embedKey) params.set('embedKey', embedKey)
      const apiKey = searchParams.get('apikey')
      if (apiKey) params.set('apikey', apiKey)
      params.set('embed', 'true')
    }

    const queryString = params.toString()
    const url = `/agent/${agentId}/chat/${newChatId}${queryString ? `?${queryString}` : ''}`
    // Use replaceState to update URL without triggering React Router re-render.
    // navigate() would unmount/remount the Chat component, causing duplicate
    // messages, key errors, and a failed stream resume attempt.
    window.history.replaceState(null, '', url)
  }, [agentId, isBubbleMode, isEmbedMode, searchParams, agent])

  /** Keeps standalone page title state aligned after inline chat renames. */
  const handleTitleUpdated = useCallback((nextTitle: string) => {
    setChat((currentChat) => {
      if (!currentChat) {
        return currentChat
      }

      return {
        ...currentChat,
        title: nextTitle,
      }
    })
  }, [])

  // Handle missing agentId
  if (!agentId) {
    // In embed mode, show error instead of redirecting to app
    if (isEmbedMode) {
      return (
        <AgentChatLayout>
          <div className="flex size-full items-center justify-center">
            <div className="text-center">
              <h2 className="text-lg font-semibold mb-2">Invalid Agent</h2>
              <p className="text-muted-foreground">
                Agent ID is required for this chat.
              </p>
            </div>
          </div>
        </AgentChatLayout>
      )
    }
    // In normal mode, redirect to agents page
    return <Navigate to={agent?.appId ? `/app/${agent.appId}/agents` : '/app'} replace />
  }

  // Show loading state while loading agent
  if (agentLoading) {
    return (
      <AgentChatLayout>
        <div className="flex size-full items-center justify-center">
          {pageTitle}
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-sm text-muted-foreground">Loading agent...</p>
          </div>
        </div>
      </AgentChatLayout>
    )
  }

  // Show error if agent not found / no access.
  // Embed widgets show a minimal inline message; standalone visitors get a clean
  // full-screen access screen instead of the half-rendered chat shell (sidebar +
  // "Loading chat history…") that looked broken in this state.
  if (agentError || !agent) {
    if (isEmbedMode) {
      return (
        <AgentChatLayout>
          <div className="flex size-full items-center justify-center">
            {pageTitle}
            <div className="text-center">
              <h2 className="text-lg font-semibold mb-2">Agent not available</h2>
              <p className="text-muted-foreground">
                This agent doesn't exist or you don't have access to it.
              </p>
            </div>
          </div>
        </AgentChatLayout>
      )
    }

    return (
      <>
        {pageTitle}
        <AgentAccessRequired mode="no-access" />
      </>
    )
  }

  // Check if agent is published (only published agents can be used in standalone mode)
  // Show fullscreen 404 page for unpublished agents
  if (!agent.published) {
    return <DomainNotFoundPage />
  }

  // Show loading state while loading chat data
  if (loading && !isNewChat) {
    return (
      <AgentChatLayout>
        <div className="flex size-full items-center justify-center">
          {pageTitle}
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-sm text-muted-foreground">Loading chat...</p>
          </div>
        </div>
      </AgentChatLayout>
    )
  }

  // Show error state
  if (error) {
    return (
      <AgentChatLayout>
        <div className="flex size-full items-center justify-center">
          {pageTitle}
          <div className="text-center">
            <h2 className="text-lg font-semibold mb-2">Error</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => navigate(`/agent/${agentId}/chat/new`)}
              className="text-primary hover:underline"
            >
              Start new chat
            </button>
          </div>
        </div>
      </AgentChatLayout>
    )
  }

  // Embed: wait for the embed config before deciding gate-vs-chat, so the
  // gate never flashes after the chat (or vice versa). Fast public endpoint;
  // failures resolve to "no gate".
  if (isEmbedMode && !embedConfigResolved) {
    return (
      <AgentChatLayout>
        <div className="flex size-full items-center justify-center">
          {pageTitle}
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AgentChatLayout>
    )
  }

  // Embed language gate — nothing else loads until a language is chosen
  // (spec 075 — Phase 3, FR-3).
  if (needsLanguageGate) {
    return (
      <AgentChatLayout>
        {pageTitle}
        <EmbedLanguageGate
          languages={gateLanguages}
          agentName={agent.name}
          agentAvatar={agent.avatar}
          onSelected={() => setGatePassedAt(Date.now())}
        />
      </AgentChatLayout>
    )
  }

  return (
    <AgentChatLayout>
      {pageTitle}
      <InteractiveChatProvider enabled>
        <Chat
          key={`${agentId}-${chatId}-${isBubbleMode ? 'bubble' : 'normal'}`}
          id={chatId}
          title={isBubbleMode ? "" : (chat?.title || `Chat with ${agent.name}`)}
          initialMessages={isNewChat || isBubbleMode ? [] : messages}
          autoResume={isBackgroundStreaming}
          sandboxId={chat?.sandboxId}
          agentId={agentId}
          onNewChat={isNewChat || isBubbleMode ? handleNewChatCreated : undefined}
          onTitleUpdated={handleTitleUpdated}
        />
      </InteractiveChatProvider>
    </AgentChatLayout>
  )
}

StandaloneAgentChatPage.displayName = 'StandaloneAgentChatPage'
